import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { TRAJECTORY_DEFAULT_PARAMS, type TrajectoryParams } from './trajectory.model';

/** Small epsilon for numerical derivative calculation */
const DERIVATIVE_EPSILON = 0.0001;

/**
 * Creates a trajectory curve that follows a parent function.
 *
 * The trajectory is offset perpendicular to the parent curve by a constant
 * visual distance, with an optional arrow indicator showing direction.
 */
export class TrajectoryCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'trajectory';

  create(
    id: string,
    def: FeatureElementDef<'trajectory'>,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as TrajectoryParams | undefined;

    if (!params?.of) {
      throw new Error('Trajectory feature requires params.of to specify the parent function');
    }

    const parentFn = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    const rawXStart = params.xStart ?? TRAJECTORY_DEFAULT_PARAMS.xStart;
    const rawXEnd = params.xEnd ?? TRAJECTORY_DEFAULT_PARAMS.xEnd;
    const distance = params.distance ?? TRAJECTORY_DEFAULT_PARAMS.distance ?? 0.5;
    const arrowDirection = params.arrowDirection ?? TRAJECTORY_DEFAULT_PARAMS.arrowDirection ?? 'end';

    // Resolve xStart/xEnd via valueContext for reactive expression support
    const { valueContext } = context;
    const getXStart = valueContext
      ? valueContext.createThunk(rawXStart)
      : () => (typeof rawXStart === 'number' ? rawXStart : NaN);
    const getXEnd = valueContext
      ? valueContext.createThunk(rawXEnd)
      : () => (typeof rawXEnd === 'number' ? rawXEnd : NaN);

    const curveStyle = resolveFeatureStyle('curve', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    // Constant visual gap: scale y-offset by normal length to compensate for slope
    // At flat areas: normalLength ≈ 1, offset = distance
    // At steep areas: normalLength > 1, offset increases to maintain visual gap
    const evaluate = (x: number) => {
      const y = parentFn.evaluate(x);
      // Numerical derivative
      const dy =
        (parentFn.evaluate(x + DERIVATIVE_EPSILON) - parentFn.evaluate(x - DERIVATIVE_EPSILON)) /
        (2 * DERIVATIVE_EPSILON);
      const normalLength = Math.sqrt(1 + dy * dy);
      return y + distance * normalLength;
    };

    // Use label text as curve name if provided (enables label rendering)
    const labelConfig = curveStyle.label;
    const labelText = typeof labelConfig === 'object' ? labelConfig.text : undefined;

    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      domain: [getXStart, getXEnd],
      name: labelText,
      style: {
        ...curveStyle,
        visible,
        arrows: {
          first: arrowDirection === 'start' || arrowDirection === 'both',
          last: arrowDirection === 'end' || arrowDirection === 'both',
        },
      },
    });
    objects.push(curveResult.curve);

    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    return {
      id,
      type: 'trajectory',
      objects,
      getParams: () => ({ xStart: getXStart(), xEnd: getXEnd(), distance }),
      setParams: () => {
        board.update();
      },
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
